import { useEffect, useState } from "react";
import type { Meta, Patient, SessionUser } from "../types";
import { clearPhone, ru } from "../docs/helpers";
import { META } from "../docs/meta";

/**
 * Чат в мессенджере MAX по номеру: у MAX нет задокументированной ссылки
 * «на диалог по номеру» (как wa.me), поэтому формат — в одном месте, чтобы
 * поменять, когда он появится или изменится.
 */
const MAX_CHAT_URL = "https://max.ru/";

type HoverModal =
  | { pat: number; kind: "manager" }
  | { pat: number; kind: "phone" }
  | { pat: number; kind: "doc" | "policy" | "snils"; title: string }
  | null;

/**
 * Port of pats_show() in inc/functions.php. Same DOM, same classes — the hover
 * cards, badges and pager are all styled by the legacy stylesheet.
 */
/**
 * QR «позвонить» — рисуется здесь, а не сторонним сервисом: телефон пациента
 * не должен уходить на чужой сервер ради картинки. Кодировщик подгружается
 * при первом наведении.
 */
function QrImage({ text }: { text: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    import("qrcode")
      .then((qr) => qr.toDataURL(text, { margin: 1, width: 160 }))
      .then((url) => alive && setSrc(url))
      .catch(() => alive && setSrc(null));
    return () => {
      alive = false;
    };
  }, [text]);
  return src ? <img src={src} alt="QR для звонка" /> : <span className="pat-qr-wait" />;
}

export default function PatientList({
  patients,
  meta,
  user,
  loading,
  loadingMore,
  hasMore,
  selectedId,
  showOkt,
  total,
  emptyHint = null,
  onSelect,
  onEdit,
  onDelete,
}: {
  patients: Patient[];
  meta: Meta;
  user: SessionUser;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  selectedId: number | null;
  showOkt: boolean;
  total: number;
  /** Под «Записей не найдено» — что ещё можно попробовать. */
  emptyHint?: React.ReactNode;
  onSelect: (p: Patient) => void;
  onEdit: (p: Patient) => void;
  onDelete: (p: Patient) => void;
}) {
  // The legacy hover cards were driven by jQuery mouseenter; here one state
  // slot tracks the open card (row + which card).
  const [hover, setHover] = useState<HoverModal>(null);
  if (loading) {
    return (
      <div className="pat-table">
        <div className="pat-row-empty">Загрузка…</div>
      </div>
    );
  }

  if (patients.length === 0) {
    return (
      <div className="pat-table">
        <div className="pat-row-empty">
          Записей не найдено.
          {emptyHint}
        </div>
      </div>
    );
  }

  return (
    <div className="pat-table">
      {patients.map((pat) => {
        const waPhone = clearPhone(pat.phone);
        const waText = encodeURIComponent(
          (meta.site.waTemplate || META.site.waTemplate).replace("[ФИО]", pat.fio)
        );
        return (
          <div
            className={`pat-row${selectedId === pat.pat_id ? " active" : ""}`}
            id={`p${pat.pat_id}`}
            key={pat.pat_id}
            onClick={() => onSelect(pat)}
          >
            <div
              className="col-1"
              onMouseEnter={() => setHover({ pat: pat.pat_id, kind: "manager" })}
              onMouseLeave={() => setHover(null)}
            >
              {ru(pat.created_at)}
              <div
                className="modal"
                style={
                  hover?.kind === "manager" && hover.pat === pat.pat_id
                    ? { display: "block" }
                    : undefined
                }
              >
                <span>{pat.fio}</span>
                Добавлен(а) менеджером: <br />
                <strong>{pat.added_by ?? "—"}</strong>
                <div>
                  Источник привлечения: <br />
                  <strong>{meta.sources[String(pat.source ?? "")] ?? "—"}</strong>
                </div>
              </div>
            </div>

            <div className="col-2">
              {showOkt && pat.has_okt && <div className="col-2-okt" />}
              <span>{pat.fio}</span>
              {pat.trustee ? (
                <div className="trustee" title="Опекун/представитель">
                  {pat.trustee}
                </div>
              ) : null}
            </div>

            <div className="col-3">{ru(pat.bdate)}</div>

            <div
              className="col-4"
              onMouseEnter={() => setHover({ pat: pat.pat_id, kind: "phone" })}
              onMouseLeave={() => setHover(null)}
            >
              <div className="col-4-phone">{pat.phone}</div>
              {hover?.kind === "phone" && hover.pat === pat.pat_id && waPhone.length >= 11 && (
                <div className="modal" style={{ display: "block" }}>
                  <div />
                  <span>{pat.fio}</span>
                  <QrImage text={`tel:8${waPhone.slice(1)}`} />
                </div>
              )}
            </div>

            <div className="col-5" title={[pat.town, pat.address].filter(Boolean).join(", ")}>
              {!pat.town && !pat.address ? (
                "—"
              ) : (
                <>
                  <div>{pat.town}</div>
                  <span>{pat.address}</span>
                </>
              )}
            </div>

            <div className="col-6">
              {!pat.doc_number && !pat.policy_number && !pat.snils ? (
                "—"
              ) : (
                <>
                  {pat.doc_number ? (
                    <div
                      className="pat-row-doc doc"
                      onMouseEnter={() =>
                        setHover({
                          pat: pat.pat_id,
                          kind: "doc",
                          title: meta.docTypes[String(pat.doc_type ?? "")] ?? "Документ",
                        })
                      }
                      onMouseLeave={() => setHover(null)}
                    >
                      {meta.docTypes[String(pat.doc_type ?? "")] ?? "Документ"}
                    </div>
                  ) : null}
                  {pat.policy_number ? (
                    <div
                      className="pat-row-doc policy"
                      onMouseEnter={() =>
                        setHover({ pat: pat.pat_id, kind: "policy", title: "Полис" })
                      }
                      onMouseLeave={() => setHover(null)}
                    >
                      Полис
                    </div>
                  ) : null}
                  {pat.snils ? (
                    <div
                      className="pat-row-doc snils"
                      onMouseEnter={() =>
                        setHover({ pat: pat.pat_id, kind: "snils", title: "СНИЛС" })
                      }
                      onMouseLeave={() => setHover(null)}
                    >
                      СНИЛС
                    </div>
                  ) : null}
                  {(hover?.kind === "doc" || hover?.kind === "policy" || hover?.kind === "snils") &&
                    hover.pat === pat.pat_id && (
                      <div className="modal" style={{ display: "block" }}>
                        <h2>{hover.title}</h2>
                        <span>{pat.fio}</span>
                        {hover.kind === "doc" && (
                          <div className="modal-doc" style={{ display: "block" }}>
                            <strong>Серия и номер:</strong>
                            {`${pat.doc_seria} ${pat.doc_number}`}
                            {pat.doc_org ? (
                              <>
                                <strong>Выдан:</strong>
                                {pat.doc_org}
                              </>
                            ) : null}
                            {pat.doc_date ? (
                              <>
                                <strong>Дата выдачи:</strong>
                                {ru(pat.doc_date)}
                              </>
                            ) : null}
                          </div>
                        )}
                        {hover.kind === "policy" && (
                          <div className="modal-policy" style={{ display: "block" }}>
                            <strong>Серия и номер:</strong>
                            {`${pat.policy_seria} ${pat.policy_number}`}
                            <strong>Выдан:</strong>
                            {pat.policy_org}
                            {pat.policy_date ? (
                              <>
                                <strong>Дата выдачи:</strong>
                                {ru(pat.policy_date)}
                              </>
                            ) : null}
                          </div>
                        )}
                        {hover.kind === "snils" && (
                          <div className="modal-snils" style={{ display: "block" }}>
                            <strong>Номер:</strong>
                            {pat.snils}
                          </div>
                        )}
                      </div>
                    )}
                </>
              )}
            </div>

            <div className="col-7">
              {user.can_edit && (
                <div
                  className="pat-edit"
                  title="Редактировать"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit(pat);
                  }}
                />
              )}
              {user.can_delete && (
                <div
                  className="pat-del"
                  title="Удалить"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(pat);
                  }}
                />
              )}
            </div>

            {/* Найден по диагнозу — какой именно диагноз подошёл: без этого
                пришлось бы открывать каждого, чтобы понять, почему он здесь. */}
            {pat.diag_match ? (
              <div className="pat-row-diag" title={pat.diag_match.text}>
                {pat.diag_match.text.slice(0, pat.diag_match.from)}
                <mark>{pat.diag_match.text.slice(pat.diag_match.from, pat.diag_match.to)}</mark>
                {pat.diag_match.text.slice(pat.diag_match.to)}
              </div>
            ) : null}

            <div className="col-8">
              <div className="col-9">
                {pat.doctor ? (
                  <>
                    Врач: <strong>{pat.doctor}</strong>
                  </>
                ) : null}
              </div>
              <div className="col-10">
                {waPhone.length >= 11 && (
                  <a
                    className="col-10-wa"
                    href={`https://wa.me/${waPhone}?text=${waText}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Написать в WhatsApp
                  </a>
                )}
                {waPhone.length >= 11 && (
                  <a
                    className="col-10-max"
                    href={`${MAX_CHAT_URL}+${waPhone}`}
                    target="_blank"
                    rel="noreferrer"
                    title="Открыть чат с пациентом в мессенджере MAX"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Написать в MAX
                  </a>
                )}
                {pat.email ? (
                  <a
                    href={`mailto:${pat.email}`}
                    className="col-10-em"
                    title={pat.email}
                    onClick={(e) => e.stopPropagation()}
                  >
                    Отправить почту
                  </a>
                ) : null}
              </div>
              <div className="col-11">{pat.comment}</div>
            </div>
          </div>
        );
      })}

      <div className="pat-lazy-footer">
        {loadingMore ? (
          <span className="pat-lazy-loading">Загрузка…</span>
        ) : hasMore ? (
          <span>
            Показано {patients.length} из {total} — прокрутите вниз, чтобы загрузить ещё
          </span>
        ) : (
          <span>Всего: {total}</span>
        )}
      </div>
    </div>
  );
}
